import { lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter, Redirect, Link } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AgentProvider, useAgent } from "@/context/agent-context";
import { PermissionsProvider, usePermissions } from "@/context/permissions-context";
import { tenantScopeFromToken } from "@/lib/payroll-op-key";
import NotFound from "@/pages/not-found";
import LoginPage from "@/pages/login";
import { OnboardingDialog } from "@/components/onboarding-dialog";
import { LangProvider, useLang } from "@/context/lang-context";
import { LanguagePicker } from "@/components/language-picker";
import { useHeartbeat } from "@/lib/heartbeat";
import { LoadingPage } from "@/components/loading-page";

// كل الصفحات كسولة التحميل (code splitting): الحزمة تنقسم لأجزاء صغيرة وكل
// صفحة تحمّل كودها عند فتحها فقط — بدل ملف واحد ضخم يبطّئ أول فتح للموقع.
// صفحة الدخول وNotFound فقط تبقيان ضمن الجزء الأساسي (أول ما يراه المستخدم).
const Inbox = lazy(() => import("@/pages/inbox"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Templates = lazy(() => import("@/pages/templates"));
const Media = lazy(() => import("@/pages/media"));
const Team = lazy(() => import("@/pages/team"));
const Reports = lazy(() => import("@/pages/reports"));
const Settings = lazy(() => import("@/pages/settings"));
const Customers = lazy(() => import("@/pages/customers"));
const Customer360 = lazy(() => import("@/pages/customer-360"));
const PbxCalls = lazy(() => import("@/pages/pbx-calls"));
const AssetsPage = lazy(() => import("@/pages/assets"));
const CompanyPapersPage = lazy(() => import("@/pages/company-docs"));
// مساران لصفحةٍ واحدة بتبويبين: «أوراق الشركة» للمدير والمشرف، و«معاملات
// الشركة» يفتحها كذلك الموظّف الموكَّل ليقفل ما وُكِّل به. لفافتان ثابتتان
// على مستوى الوحدة — دالّة مجهولة داخل JSX تعيد تركيب الصفحة عند كل رسم.
function CompanyDocsRoute() { return <CompanyPapersPage initial="docs" />; }
function CompanyFormsRoute() { return <CompanyPapersPage initial="forms" />; }
function CompanyErrandsRoute() { return <CompanyPapersPage initial="errands" />; }
const Campaigns = lazy(() => import("@/pages/campaigns"));
const Bookings = lazy(() => import("@/pages/bookings"));
const Contracts = lazy(() => import("@/pages/contracts"));
const ContractsFinance = lazy(() => import("@/pages/contracts-finance"));
const RenterCheck = lazy(() => import("@/pages/renter-check"));
const Inventory = lazy(() => import("@/pages/inventory"));
const ExpensesReport = lazy(() => import("@/pages/expenses-report"));
const CollectionPage = lazy(() => import("@/pages/collection"));
const CollectionTargetPage = lazy(() => import("@/pages/collection-target"));
const MaintenanceLogPage = lazy(() => import("@/pages/maintenance-log"));
const FleetExitPage = lazy(() => import("@/pages/fleet-exit"));
const ReceiptApprovalPage = lazy(() => import("@/pages/receipt-approval"));
const FleetExitDetailPage = lazy(() => import("@/pages/fleet-exit-detail"));
const DetailedReports = lazy(() => import("@/pages/detailed-reports"));
const Commissions = lazy(() => import("@/pages/commissions"));
const ErpEmployees = lazy(() => import("@/pages/erp-employees"));
const Cars = lazy(() => import("@/pages/cars"));
const CarProfitability = lazy(() => import("@/pages/car-profitability"));
const CarMonthlyReport = lazy(() => import("@/pages/car-monthly-report"));
const DeviceInvoices = lazy(() => import("@/pages/device-invoices"));
const LimoBookings = lazy(() => import("@/pages/limo-bookings"));
const Payments = lazy(() => import("@/pages/payments"));
const Knowledge = lazy(() => import("@/pages/knowledge"));
const ReplyLab = lazy(() => import("@/pages/reply-lab"));
const ChequesPage = lazy(() => import("@/pages/cheques"));
const DispatchPage = lazy(() => import("@/pages/dispatch"));
const CustomerAppPage = lazy(() => import("@/pages/customer-app"));
const DriverTripsPage = lazy(() => import("@/pages/driver-trips"));
const PublicTrip = lazy(() => import("@/pages/public-trip"));
const Tracking = lazy(() => import("@/pages/tracking"));
const TempMonitoring = lazy(() => import("@/pages/temp-monitoring"));
const Maintenance = lazy(() => import("@/pages/maintenance"));
const AuditLog = lazy(() => import("@/pages/audit-log"));
const Integrations = lazy(() => import("@/pages/integrations"));
const PublicTracking = lazy(() => import("@/pages/public-tracking"));
const PublicPlayback = lazy(() => import("@/pages/public-playback"));
const PrivacyPage = lazy(() => import("@/pages/privacy"));
const TermsPage = lazy(() => import("@/pages/terms"));
const AttendPage = lazy(() => import("@/pages/attend"));
const SignupPage = lazy(() => import("@/pages/signup"));
const MobileSso = lazy(() => import("@/pages/mobile-sso"));
const WelcomePage = lazy(() => import("@/pages/welcome"));
const AdminPage = lazy(() => import("@/pages/admin"));
const MasterAdmin = lazy(() => import("@/pages/master-admin"));
const Sla = lazy(() => import("@/pages/sla"));
const AiUsage = lazy(() => import("@/pages/ai-usage"));
const SubscriptionPage = lazy(() => import("@/pages/subscription"));
const Attendance = lazy(() => import("@/pages/attendance"));
const AttendanceReview = lazy(() => import("@/pages/attendance-review"));
const CheckIn = lazy(() => import("@/pages/check-in"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        if ((error as { status?: number })?.status === 401) return false;
        return failureCount < 3;
      },
    },
  },
});

const ROLE_RANK: Record<string, number> = { admin: 3, supervisor: 2, agent: 1 };

// Landing preference, most-wanted first. The chatbox (inbox) stays the default
// home; a user whose company/role can't see it is routed to the first page
// they ARE permitted to use instead of hitting "غير مصرح" right after login.
const LANDING_PRIORITY: { path: string; feature: string; minRole?: "supervisor" | "admin" }[] = [
  { path: "/", feature: "inbox" },
  { path: "/tracking", feature: "tracking" },
  { path: "/maintenance", feature: "maintenance" },
  { path: "/dashboard", feature: "dashboard" },
  { path: "/customers", feature: "customers" },
  { path: "/bookings", feature: "bookings" },
  { path: "/contracts", feature: "contracts" },
  { path: "/renter-check", feature: "contracts" },
  { path: "/inventory", feature: "inventory" },
  { path: "/cars", feature: "cars" },
  { path: "/payments", feature: "payments" },
  { path: "/campaigns", feature: "campaigns" },
  { path: "/templates", feature: "templates" },
  { path: "/media", feature: "media" },
  { path: "/knowledge", feature: "knowledge" },
  { path: "/reply-lab", feature: "settings" },
  { path: "/sla", feature: "sla" },
  { path: "/attendance", feature: "attendance" },
  { path: "/reports", feature: "reports", minRole: "supervisor" },
  { path: "/team", feature: "team" },
  { path: "/ai-usage", feature: "ai_usage" },
  { path: "/settings", feature: "settings" },
  { path: "/audit-log", feature: "audit_log" },
];

function firstAllowedPath(canView: (f: string) => boolean, role: string | null, hasFeature: (f: string) => boolean = () => true): string | null {
  for (const p of LANDING_PRIORITY) {
    if (!canView(p.feature)) continue;
    // صفحةُ العقود مخفيّةٌ عن شركات «منظور الأسطول» (`contracts_ledger`) — لا يُهبَط عليها.
    if (p.path === "/contracts" && !hasFeature("contracts_ledger")) continue;
    if (p.minRole && (ROLE_RANK[role ?? ""] ?? 0) < ROLE_RANK[p.minRole]) continue;
    return p.path;
  }
  return null;
}


function Forbidden() {
  const { canView, hasFeature, isLoaded } = usePermissions();
  const { storedAgent } = useAgent();
  const fallback = isLoaded ? firstAllowedPath(canView, storedAgent?.role ?? null, hasFeature) : null;
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4 text-slate-500">
      <div className="text-6xl">🚫</div>
      <h2 className="text-xl font-bold text-slate-700">غير مصرح</h2>
      <p className="text-sm">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      {fallback && (
        <Link href={fallback}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-5 py-2.5">
          الانتقال إلى صفحتك الرئيسية
        </Link>
      )}
    </div>
  );
}

// The home route: the chatbox when permitted, otherwise an automatic redirect
// to the user's first permitted page — so logging in never opens on a 🚫.
function HomeRoute() {
  const { canView, hasFeature, isLoaded } = usePermissions();
  const { storedAgent } = useAgent();
  if (!isLoaded) return null;
  if (canView("inbox")) return <Inbox />;
  const fallback = firstAllowedPath(canView, storedAgent?.role ?? null, hasFeature);
  if (fallback && fallback !== "/") return <Redirect to={fallback} />;
  return <Forbidden />;
}

function ProtectedRoute({ feature, module, component: Component, minRole }: { feature: string; module?: string; component: React.ComponentType; minRole?: "supervisor" | "admin" }) {
  const { canView, hasFeature, isLoaded } = usePermissions();
  const { storedAgent } = useAgent();
  if (!isLoaded) return null;
  if (!canView(feature)) return <Forbidden />;
  // وحدةُ شركةٍ منفصلةٌ عن مفتاح الصلاحيّة (مثل «الاستعلام عن مستأجر» تحت «العقود»).
  if (module && !hasFeature(module)) return <Forbidden />;
  // Some pages are manager-level even when their feature flag is shared (e.g.
  // car-profitability shares the "cars" feature with the cars list).
  if (minRole && (ROLE_RANK[storedAgent?.role ?? ""] ?? 0) < ROLE_RANK[minRole]) return <Forbidden />;
  return <Component />;
}

function Router() {
  return (
    <Switch>
      <Route path="/"><HomeRoute /></Route>
      <Route path="/check-in" component={CheckIn} />
      <Route path="/dashboard"><ProtectedRoute feature="dashboard" component={Dashboard} /></Route>
      <Route path="/templates"><ProtectedRoute feature="templates" component={Templates} /></Route>
      <Route path="/media"><ProtectedRoute feature="media" component={Media} /></Route>
      <Route path="/team"><ProtectedRoute feature="team" component={Team} /></Route>
      <Route path="/reports"><ProtectedRoute feature="reports" minRole="supervisor" component={Reports} /></Route>
      <Route path="/customers"><ProtectedRoute feature="customers" component={Customers} /></Route>
      <Route path="/customer-360/:id"><ProtectedRoute feature="customers" component={Customer360} /></Route>
      <Route path="/pbx-calls"><ProtectedRoute feature="pbx_calls" component={PbxCalls} /></Route>
      <Route path="/assets"><ProtectedRoute feature="assets" component={AssetsPage} /></Route>
      <Route path="/company-docs"><ProtectedRoute feature="company_docs" minRole="supervisor" component={CompanyDocsRoute} /></Route>
      <Route path="/company-errands"><ProtectedRoute feature="company_errands" component={CompanyErrandsRoute} /></Route>
      <Route path="/company-forms"><ProtectedRoute feature="company_forms" component={CompanyFormsRoute} /></Route>
      <Route path="/campaigns"><ProtectedRoute feature="campaigns" component={Campaigns} /></Route>
      <Route path="/bookings"><ProtectedRoute feature="bookings" component={Bookings} /></Route>
      <Route path="/contracts"><ProtectedRoute feature="contracts" module="contracts_ledger" component={Contracts} /></Route>
      <Route path="/contracts-finance"><ProtectedRoute feature="contracts_finance" component={ContractsFinance} /></Route>
      <Route path="/renter-check"><ProtectedRoute feature="contracts" module="renter_check" component={RenterCheck} /></Route>
      <Route path="/inventory"><ProtectedRoute feature="inventory" component={Inventory} /></Route>
      <Route path="/expenses-report"><ProtectedRoute feature="expenses_report" component={ExpensesReport} /></Route>
      <Route path="/collection"><ProtectedRoute feature="collection" component={CollectionPage} /></Route>
      <Route path="/collection-target"><ProtectedRoute feature="collection" component={CollectionTargetPage} /></Route>
      <Route path="/maintenance-log"><ProtectedRoute feature="maintenance_log_review" component={MaintenanceLogPage} /></Route>
      <Route path="/fleet-exit"><ProtectedRoute feature="fleet_exit" component={FleetExitPage} /></Route>
      <Route path="/receipt-approval"><ProtectedRoute feature="receipt_approval" component={ReceiptApprovalPage} /></Route>
      {/* تحليلُ سيّارةٍ واحدة — نفسُ الوحدة ونفسُ المفتاح، فلا بابَ ثانياً. */}
      <Route path="/fleet-exit/:car"><ProtectedRoute feature="fleet_exit" component={FleetExitDetailPage} /></Route>
      <Route path="/detailed-reports"><ProtectedRoute feature="detailed_reports" component={DetailedReports} /></Route>
      <Route path="/commissions"><ProtectedRoute feature="commissions" component={Commissions} /></Route>
      <Route path="/erp-employees"><ProtectedRoute feature="erp" component={ErpEmployees} /></Route>
      <Route path="/cars"><ProtectedRoute feature="cars" component={Cars} /></Route>
      <Route path="/car-profitability"><ProtectedRoute feature="cars" minRole="supervisor" component={CarProfitability} /></Route>
      <Route path="/car-monthly-report"><ProtectedRoute feature="cars" minRole="supervisor" component={CarMonthlyReport} /></Route>
      <Route path="/limo-bookings"><ProtectedRoute feature="cars" component={LimoBookings} /></Route>
      <Route path="/payments"><ProtectedRoute feature="payments" component={Payments} /></Route>
      <Route path="/device-invoices"><ProtectedRoute feature="settings" minRole="supervisor" component={DeviceInvoices} /></Route>
      <Route path="/settings"><ProtectedRoute feature="settings" component={Settings} /></Route>
      <Route path="/knowledge"><ProtectedRoute feature="knowledge" component={Knowledge} /></Route>
      <Route path="/reply-lab"><ProtectedRoute feature="settings" module="reply_suggest" component={ReplyLab} /></Route>
      <Route path="/cheques"><ProtectedRoute feature="cheques" component={ChequesPage} /></Route>
      <Route path="/dispatch"><ProtectedRoute feature="dispatch" component={DispatchPage} /></Route>
      <Route path="/customer-app"><ProtectedRoute feature="inbox" module="customer_app" component={CustomerAppPage} /></Route>
      <Route path="/driver"><ProtectedRoute feature="dispatch_driver" component={DriverTripsPage} /></Route>
      <Route path="/tracking"><ProtectedRoute feature="tracking" component={Tracking} /></Route>
      <Route path="/temp-monitoring"><ProtectedRoute feature="tracking" component={TempMonitoring} /></Route>
      <Route path="/maintenance"><ProtectedRoute feature="maintenance" component={Maintenance} /></Route>
      <Route path="/audit-log"><ProtectedRoute feature="audit_log" component={AuditLog} /></Route>
      <Route path="/integrations"><ProtectedRoute feature="settings" component={Integrations} /></Route>
      <Route path="/sla"><ProtectedRoute feature="sla" component={Sla} /></Route>
      <Route path="/attendance-review"><ProtectedRoute feature="attendance" component={AttendanceReview} /></Route>
      <Route path="/attendance"><ProtectedRoute feature="attendance" component={Attendance} /></Route>
      <Route path="/ai-usage"><ProtectedRoute feature="ai_usage" component={AiUsage} /></Route>
      <Route path="/subscription"><ProtectedRoute feature="settings" component={SubscriptionPage} /></Route>
      <Route path="/admin" component={AdminPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function PermissionsWrapper({ children }: { children: React.ReactNode }) {
  const { storedAgent } = useAgent();
  // مفتاحُ الهُويّة: المعرّف والشركةُ والدور. الدورُ وحدَه لا يميّز موظّفَين
  // بدورٍ واحد ولا شركتين لمديرٍ واحد — انظر `PermissionsProvider`.
  const principal = storedAgent
    ? `${storedAgent.id}:${tenantScopeFromToken(localStorage.getItem("waslacrm_token")) ?? ""}:${storedAgent.role ?? ""}`
    : null;
  return (
    <PermissionsProvider role={storedAgent?.role ?? null} principal={principal}>
      {children}
    </PermissionsProvider>
  );
}

function AppContent() {
  const { isAuthenticated, login } = useAgent();
  useHeartbeat(isAuthenticated);

  // Public invitation acceptance page (/master/setup) is served by its own
  // dedicated, third-party-JS-free HTML entry (master-setup.html) — the SPA no
  // longer handles it, so it must never be matched by the /master check below.

  // Master-admin console has its own platform login, independent of agent auth.
  if (window.location.pathname.startsWith("/master")) {
    return <MasterAdmin />;
  }

  // Public self-service signup (no auth) — buy a plan, pay, get a company provisioned.
  if (window.location.pathname.startsWith("/signup")) {
    return <SignupPage />;
  }

  // Post-payment auto-login landing (exchanges the one-time signup token).
  if (window.location.pathname.startsWith("/welcome")) {
    return <WelcomePage />;
  }

  if (window.location.pathname.startsWith("/privacy")) {
    return <PrivacyPage />;
  }

  if (window.location.pathname.startsWith("/terms")) {
    return <TermsPage />;
  }

  // Employee attendance (mobile/Capacitor app) — own employee login, no agent auth.
  // Exact match so it does NOT swallow the admin "/attendance" route.
  if (window.location.pathname === "/attend" || window.location.pathname.startsWith("/attend/")) {
    return <AttendPage />;
  }

  // Mobile-app WebView SSO landing — exchanges a one-time token for a web
  // session so the app opens admin pages without a second login.
  if (window.location.pathname.startsWith("/mobile-sso")) {
    return <MobileSso />;
  }

  // رابطُ تتبّع الرحلة للعميل — عامٌّ بلا تسجيل دخول، خاصٌّ بالرحلة لا بالسيّارة.
  if (window.location.pathname.startsWith("/trip/")) {
    return <PublicTrip />;
  }

  if (window.location.pathname.startsWith("/track/")) {
    return <PublicTracking />;
  }

  if (window.location.pathname.startsWith("/playback/")) {
    return <PublicPlayback />;
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={login} />;
  }

  return (
    <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
      <Router />
      <OnboardingDialog />
      {/* أوّل دخولٍ لمن لا لغةَ محفوظة له. بعد المصادقة عمداً: قبلها لا نعرف
          من هو، ولا موضع نحفظ فيه اختياره. */}
      <LanguagePicker />
    </WouterRouter>
  );
}

/** الاتجاه يتبع اللغة: العربية RTL والإنجليزية LTR. كان `dir="rtl"` ثابتاً،
 *  فالإنجليزية داخله تُقرأ من اليمين — الأرقام والجمل تقفز أماكنها. */
function DirShell() {
  const { lang } = useLang();
  return (
    /* `key={lang}`: تبديل اللغة يُعيد بناء الشجرة، فلا يبقى مكوّنٌ محفوظ
       (`memo`/`useMemo`) على اللغة القديمة. التبديل نادر، والثمن لا يُذكر. */
    <div key={lang} dir={lang === "ar" ? "rtl" : "ltr"}>
      <Suspense fallback={<LoadingPage />}>
        <AppContent />
      </Suspense>
      <SonnerToaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AgentProvider>
          <PermissionsWrapper>
            <LangProvider>
              <DirShell />
            </LangProvider>
          </PermissionsWrapper>
        </AgentProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
